import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSymptom, createReport } from "../lib/api";
import { CliBlock } from "../components/CliBlock";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  ChevronLeft, ChevronRight, Route, CheckCircle2, AlertTriangle,
  Compass, MessageSquare, Bookmark, Check, Share2, ImagePlus, Sparkles,
} from "lucide-react";

export default function Wizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState("");
  const [done, setDone] = useState(new Set());
  const [saveOpen, setSaveOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [siteName, setSiteName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const toggleDone = (stepId) =>
    setDone((prev) => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });

  const saveReport = async () => {
    setSaving(true);
    try {
      const r = await createReport({
        symptom_id: id,
        completed_steps: Array.from(done),
        notes: notes.trim() || null,
        site_name: siteName.trim() || null,
        logo_url: logoUrl || null,
      });
      setSaved(r);
      toast.success("Report saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const copyShare = () => {
    if (!saved) return;
    navigator.clipboard.writeText(`${window.location.origin}/report/${saved.share_id}`);
    toast.success("Share link copied");
  };

  // AI Deep-Dive: hand a specific step to the Assistant with full context pre-loaded.
  const askAboutStep = (step, idx) => {
    if (!data) return;
    const prompt =
      `I'm troubleshooting "${data.title}" and I'm on step ${idx + 1}: "${step.title}".\n\n` +
      `Step guidance: ${step.description}\n\n` +
      `Give me a tailored, field-ready fix for this specific step: exact CLI / PowerShell commands, ` +
      `precise CUCM/RTMT or Webex Control Hub / Teams Admin Center paths, what good output looks like, ` +
      `and the most likely root causes if this step fails.`;
    navigate(`/assistant?context=${encodeURIComponent(data.title + " · " + step.title)}&ask=${encodeURIComponent(prompt)}`);
  };

  useEffect(() => {
    setLoading(true);
    getSymptom(id)
      .then((d) => {
        const want = new URLSearchParams(window.location.search).get("step");
        const target = d.steps?.find((s) => s.id === want);
        setData(d); setOpen(target?.id || d.steps?.[0]?.id || ""); setLoading(false);
        if (target) setTimeout(() => document.querySelector(`[data-testid="step-${target.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-8 text-zinc-500 font-mono text-sm">Loading diagnostic flow…</div>;
  if (!data) return <div className="p-8 text-zinc-400">Symptom not found. <button onClick={() => navigate("/")} className="text-blue-400">Back</button></div>;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10 gap-3">
        <button onClick={() => navigate("/")} data-testid="wizard-back-btn" className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Diagnostics
        </button>
        <ChevronRight className="h-3 w-3 text-zinc-600" />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold truncate">{data.title}</span>
        <button
          onClick={() => { setSaved(null); setSaveOpen(true); }}
          data-testid="wizard-save-report-btn"
          className="ml-auto flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0"
        >
          <Bookmark className="h-4 w-4" /> Save Report
        </button>
      </header>

      <div className="p-5 sm:p-8 max-w-4xl">
        {/* Overview */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
            <Route className="h-4 w-4" /> Diagnostic Flow
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-white mt-3">{data.title}</h1>
          <p className="text-zinc-400 mt-3 leading-relaxed">{data.summary}</p>

          <div className="flex flex-wrap gap-2 mt-5">
            {data.layers.map((l) => (
              <span key={l} className="text-[11px] font-mono px-2.5 py-1 rounded-md border border-zinc-700 bg-zinc-950 text-zinc-300">{l}</span>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-3">
            Step-by-step ({data.steps.length})
          </div>
          <Accordion type="single" collapsible value={open} onValueChange={setOpen} className="space-y-3">
            {data.steps.map((step, idx) => (
              <AccordionItem
                key={step.id}
                value={step.id}
                data-testid={`step-${step.id}`}
                className="border border-zinc-800 rounded-lg bg-zinc-900 overflow-hidden px-0"
              >
                <AccordionTrigger className="px-5 hover:no-underline hover:bg-zinc-800/40 [&[data-state=open]]:bg-zinc-800/30">
                  <div className="flex items-center gap-4 text-left">
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`step-done-${step.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleDone(step.id); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); toggleDone(step.id); } }}
                      className={`h-8 w-8 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center border cursor-pointer transition-colors ${done.has(step.id) ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-blue-600/15 border-blue-600/30 text-blue-400 hover:border-emerald-500/40"}`}
                      title={done.has(step.id) ? "Marked done — click to undo" : "Mark step done"}
                    >
                      {done.has(step.id) ? <Check className="h-4 w-4" /> : idx + 1}
                    </span>
                    <div>
                      <div className="font-heading font-bold text-white text-base">{step.title}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 font-semibold">{step.layer}{done.has(step.id) ? " · done" : ""}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <p className="text-sm text-zinc-400 leading-relaxed mb-4">{step.description}</p>

                  {step.cli_commands?.length > 0 && (
                    <div className="mb-4">
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">CLI Commands</div>
                      <CliBlock commands={step.cli_commands} testId={`cli-${step.id}`} />
                    </div>
                  )}

                  {step.navigation?.length > 0 && (
                    <div className="mb-4">
                      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">CUCM / RTMT Navigation</div>
                      <div className="space-y-2">
                        {step.navigation.map((nav, i) => (
                          <div key={i} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                            <div className="flex items-start gap-2">
                              <Compass className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                              <div>
                                <div className="text-[10px] uppercase tracking-wider text-blue-400 font-semibold">{nav.system}</div>
                                <div className="font-mono text-[13px] text-zinc-200 mt-1">{nav.path}</div>
                                {nav.note && <div className="text-xs text-zinc-500 mt-1">{nav.note}</div>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {step.good_output && (
                    <div className="rounded-md bg-zinc-950 border border-zinc-800 border-l-4 border-l-emerald-500 p-3 mb-3">
                      <div className="flex items-center gap-2 text-emerald-400 text-[11px] uppercase tracking-wider font-semibold">
                        <CheckCircle2 className="h-4 w-4" /> Good Output
                      </div>
                      <p className="text-sm text-zinc-300 mt-1.5">{step.good_output}</p>
                    </div>
                  )}

                  {step.bad_signs && (
                    <div className="rounded-md bg-zinc-950 border border-zinc-800 border-l-4 border-l-red-500 p-3">
                      <div className="flex items-center gap-2 text-red-400 text-[11px] uppercase tracking-wider font-semibold">
                        <AlertTriangle className="h-4 w-4" /> Warning Signs
                      </div>
                      <p className="text-sm text-zinc-300 mt-1.5">{step.bad_signs}</p>
                    </div>
                  )}

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => askAboutStep(step, idx)}
                      data-testid={`ask-ai-step-${step.id}`}
                      className="inline-flex items-center gap-2 text-[13px] font-medium text-blue-400 hover:text-blue-300 border border-blue-600/30 hover:border-blue-600/50 bg-blue-600/10 hover:bg-blue-600/15 px-3 py-1.5 rounded-md transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5" /> Ask AI about this step
                    </button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5 flex items-center justify-between gap-4">
          <div className="text-sm text-zinc-400">
            Still not resolved? Ask the assistant about <span className="text-white">{data.title}</span>.
          </div>
          <button
            onClick={() => navigate(`/assistant?context=${encodeURIComponent(data.title)}`)}
            data-testid="wizard-ask-assistant-btn"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors shrink-0"
          >
            <MessageSquare className="h-4 w-4" /> Ask AI Assistant
          </button>
        </div>
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white" data-testid="save-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Save Diagnostic Report</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Bookmark this troubleshooting run and get a shareable, printable link.
            </DialogDescription>
          </DialogHeader>
          {!saved ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Bookmark this run for <span className="text-white">{data.title}</span>.
                {" "}{done.size} of {data.steps.length} steps marked done.
              </p>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Site / Customer Name (optional)</label>
                <Input
                  data-testid="report-site-input"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="e.g. Acme Corp — HQ Site B"
                  className="mt-2 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Customer Logo (optional)</label>
                <div className="mt-2 flex items-center gap-3">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 h-10 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">
                    <ImagePlus className="h-4 w-4" /> {logoUrl ? "Change logo" : "Upload logo"}
                    <input type="file" accept="image/*" onChange={onLogoChange} data-testid="report-logo-input" className="hidden" />
                  </label>
                  {logoUrl && (
                    <div className="flex items-center gap-2">
                      <img src={logoUrl} alt="logo preview" className="h-10 w-10 object-contain rounded bg-white/5 border border-zinc-800" />
                      <button onClick={() => setLogoUrl("")} data-testid="report-logo-clear" className="text-xs text-zinc-500 hover:text-red-400">Remove</button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-zinc-600 mt-1">PNG/JPG under 500KB. Appears on the printable report header.</p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Notes (optional)</label>
                <Textarea
                  data-testid="report-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Site B gateway 10.20.1.1 — RTP range was blocked on the ASA…"
                  className="mt-2 bg-zinc-950 border-zinc-800 min-h-[100px]"
                />
              </div>
              <DialogFooter>
                <button onClick={() => setSaveOpen(false)} data-testid="report-cancel-btn" className="px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">Cancel</button>
                <button onClick={saveReport} disabled={saving} data-testid="report-save-confirm-btn" className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition-colors flex items-center gap-2">
                  <Bookmark className="h-4 w-4" /> {saving ? "Saving…" : "Save Report"}
                </button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4" data-testid="report-saved-state">
              <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="h-5 w-5" /> Report saved and ready to share.</div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs text-zinc-300 break-all">
                {`${window.location.origin}/report/${saved.share_id}`}
              </div>
              <DialogFooter>
                <button onClick={copyShare} data-testid="report-copy-share-btn" className="px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors flex items-center gap-2">
                  <Share2 className="h-4 w-4" /> Copy link
                </button>
                <button onClick={() => navigate(`/report/${saved.share_id}`)} data-testid="report-open-saved-btn" className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium transition-colors">Open report</button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
